import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { auth as authApi, clearToken, getToken, setToken } from "./api";
import type { MeResponse } from "./api";

interface AuthState {
  loading: boolean;
  user: MeResponse | null;
}

export type LoginOutcome =
  | { kind: "ok"; user: MeResponse }
  | { kind: "totp_required" };

interface AuthContextValue extends AuthState {
  login: (
    email: string,
    password: string,
    totpCode?: string,
  ) => Promise<LoginOutcome>;
  logout: () => void;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    loading: Boolean(getToken()),
    user: null,
  });

  const refresh = async () => {
    if (!getToken()) {
      setState({ loading: false, user: null });
      return;
    }
    try {
      const user = await authApi.me();
      setState({ loading: false, user });
    } catch {
      clearToken();
      setState({ loading: false, user: null });
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const login = async (
    email: string,
    password: string,
    totpCode?: string,
  ): Promise<LoginOutcome> => {
    const resp = await authApi.login(email, password, totpCode);
    if (resp.requires_totp || !resp.access_token) {
      return { kind: "totp_required" };
    }
    setToken(resp.access_token);
    const user = await authApi.me();
    setState({ loading: false, user });
    return { kind: "ok", user };
  };

  const logout = () => {
    clearToken();
    setState({ loading: false, user: null });
  };

  return (
    <AuthContext.Provider value={{ ...state, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth fuera de AuthProvider");
  return ctx;
}
